import { FC, useMemo, useState } from 'react';
import { Box, Button, Checkbox, Chip, Collapse, IconButton, InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export interface IModuleNode { key: string; name: string; description?: string; subModules: { key: string; name: string }[] }

interface IModulePickerProps {
  modules: IModuleNode[];
  features?: { key: string; name: string }[];
  value: string[];
  onChange: (keys: string[]) => void;
}

const FEATURES_KEY = '__features';

// Module list for the plan form: one row per module, sub-modules expand below it
const ModulePicker: FC<IModulePickerProps> = ({ modules, features = [], value, onChange }) => {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string[]>([]);
  const selected = useMemo(() => new Set(value), [value]);

  // features are shown as one more group at the end
  const groups: IModuleNode[] = useMemo(() => [
    ...modules,
    ...(features.length ? [{ key: FEATURES_KEY, name: 'Add-on features', description: 'Features outside the main menu', subModules: features }] : []),
  ], [modules, features]);

  const term = search.trim().toLowerCase();
  const visible = groups
    .map((g) => {
      if (!term || g.name.toLowerCase().includes(term)) return g;
      const subs = g.subModules.filter((s) => s.name.toLowerCase().includes(term));
      return subs.length ? { ...g, subModules: subs } : null;
    })
    .filter(Boolean) as IModuleNode[];

  const keysOf = (g: IModuleNode) => [...(g.key === FEATURES_KEY ? [] : [g.key]), ...g.subModules.map((s) => s.key)];
  const allKeys = groups.flatMap(keysOf);
  const enabledCount = allKeys.filter((k) => selected.has(k)).length;

  const setKeys = (keys: string[], on: boolean) =>
    onChange(on ? Array.from(new Set([...value, ...keys])) : value.filter((k) => !keys.includes(k)));

  const toggleExpand = (key: string) =>
    setExpanded(expanded.includes(key) ? expanded.filter((k) => k !== key) : [...expanded, key]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <TextField
          size='small'
          placeholder='Search modules'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: '1 1 220px' }}
          InputProps={{ startAdornment: <InputAdornment position='start'><SearchIcon fontSize='small' /></InputAdornment> }}
        />
        <span style={{ fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap' }}>{enabledCount} of {allKeys.length} enabled</span>
        <Button size='small' onClick={() => setKeys(allKeys, true)} sx={{ textTransform: 'none' }}>Select all</Button>
        <Button size='small' color='inherit' onClick={() => setKeys(allKeys, false)} sx={{ textTransform: 'none', color: '#6b7280' }}>Clear all</Button>
      </Box>

      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', maxHeight: '48vh', overflowY: 'auto' }}>
        {visible.map((g, index) => {
          const keys = keysOf(g);
          const count = keys.filter((k) => selected.has(k)).length;
          const hasSubs = g.subModules.length > 0;
          const open = hasSubs && (!!term || expanded.includes(g.key));
          return (
            <Box key={g.key} sx={{ borderTop: index ? '1px solid #f0f1f3' : 'none' }}>
              <Box
                onClick={() => hasSubs && toggleExpand(g.key)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, cursor: hasSubs ? 'pointer' : 'default', '&:hover': { bgcolor: '#fafafb' } }}
              >
                <Checkbox
                  size='small'
                  checked={count === keys.length}
                  indeterminate={count > 0 && count < keys.length}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setKeys(keys, e.target.checked)}
                />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}>{g.name}</div>
                  {g.description && <div style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.description}</div>}
                </Box>
                {hasSubs && (
                  <Chip
                    size='small'
                    label={`${count} / ${keys.length}`}
                    color={count === keys.length ? 'primary' : 'default'}
                    variant={count === 0 ? 'outlined' : 'filled'}
                    sx={{ fontSize: 12, height: 22, minWidth: 52 }}
                  />
                )}
                <IconButton size='small' disabled={!hasSubs} sx={{ visibility: hasSubs ? 'visible' : 'hidden', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
                  <ExpandMoreIcon fontSize='small' />
                </IconButton>
              </Box>
              <Collapse in={open} unmountOnExit>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, columnGap: 1, px: 1, pb: 1, pl: 6, bgcolor: '#fafafb', borderTop: '1px solid #f0f1f3' }}>
                  {g.subModules.map((s) => (
                    <Box
                      key={s.key}
                      component='label'
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, fontSize: 13, color: '#374151', cursor: 'pointer', minWidth: 0 }}
                    >
                      <Checkbox size='small' sx={{ p: 0.75 }} checked={selected.has(s.key)} onChange={(e) => setKeys([s.key], e.target.checked)} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.name}>{s.name}</span>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
        {!visible.length && <Box sx={{ p: 2, fontSize: 13, color: '#6b7280' }}>No modules match "{search}".</Box>}
      </Box>
    </Box>
  );
};

export default ModulePicker;
